/* app/screens.jsx - inhabited screens.
   PostsScreen is wired to the live backend (GET/PUT/DELETE /api/wp/posts),
   scoped to the active site; markup is the original design. GenericStub stands
   in for screens being wired in later phases. */
import { api, apiList } from '../lib/api.js';
import { plainTitle } from '../lib/wp-text.js';
(function () {
  const { useState, useEffect, useCallback } = React;
  function MlIcon(props) { return React.createElement(window.MlIcon, props); }

  // ---- live-data helpers (Posts wiring) ----
  function fmtDate(iso) {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch { return String(iso); }
  }
  // WP post → the row shape the table renders. seo/words aren't in the list
  // response (same as the production app) → shown as "-". `author` is the name,
  // filled in from the site's user list once that is read, so it starts empty.
  //
  // `updated` is the MODIFIED date. It used to be the publish date under an
  // "Updated" heading, which filed a post edited this morning wherever it first
  // went out. Both dates are kept: the column shows one, either can sort.
  function mapPost(p) {
    const s = p.status;
    const status = s === "publish" ? "published" : s === "pending" ? "review" : s === "trash" ? "trash" : "draft";
    const mod = p.modified || p.date || "";
    return {
      id: p.id,
      // Decoded: `title.rendered` is HTML, so a curly apostrophe arrives as
      // `&#8217;` and would be shown as those six characters.
      title: plainTitle(p.title) || "(no title)",
      slug: p.slug ? "/" + p.slug : "",
      author: "", authorId: p.author != null ? p.author : null,
      cats: Array.isArray(p.categories) ? p.categories : [],
      tags: Array.isArray(p.tags) ? p.tags : [],
      seo: null, words: null,
      status, updated: fmtDate(mod), modifiedRaw: mod, dateRaw: p.date || "",
      link: p.link || "", _raw: p,
    };
  }
  const siteUrlOf = (site) => {
    const u = site && (site.urlFull || site.url);
    if (!u) return null;
    return u.startsWith("http") ? u : "https://" + u;
  };

  /* ── A row's identity on screen is NOT its post id ──
     Two sections of one website hold colliding post ids freely: id 12 exists in
     both blogs of the live property. A table keyed on the post id would give
     two rows the same React key, and a selection held as post ids would tick
     one section's row by ticking another's. So every row carries `rowKey`,
     which is the post id at a section and `section:id` at All, and the table,
     the selection and the bulk bar use that and nothing else.

     `siteUrl` is the second half of the same problem and the more expensive
     one: a post id means something only inside the CMS it came from, so a write
     carrying one section's id and another section's address edits whatever post
     happens to hold that id in the wrong website. The endpoint puts the address
     ON THE ROW, and this never falls back to the anchor's. */
  const withSection = (row, p) => ({
    ...row,
    rowKey: p.row_key || String(row.id),
    // WHICH LENS THIS ROW CAME THROUGH. A page a CMS holds and a page the
    // website SERVES are both pages, and on one list the difference has to be
    // on the row: the second has no post id, no author and no modified date,
    // because nothing in a CMS is backing it. `listedWhy` is the server's
    // sentence, which says whether the piece SERVES this page or merely FOUND
    // it (MODULE-SCOPE 7).
    readFrom: p.read_from || "cms",
    listedWhy: p.listed_why || null,
    sectionId: p.section_id == null ? null : p.section_id,
    sectionName: p.section_name || "",
    sectionSource: p.section_source || "",
    canPublish: !!p.can_publish,
    canEdit: p.can_edit !== false,
    verb: p.verb || "edit",
    verbWhy: p.verb_why || null,
    siteUrl: p.site_url || null,
  });
  const mapUnionPost = (p) => withSection(mapPost(p), p);

  // ---- Row "⋯" overflow menu (Posts + Pages) ----
  // Same actions the pre-React app offered (frontend/js/posts.js): status-aware,
  // anchored under the button, destructive actions go through a confirm dialog.
  // `kind` values: edit | publish | trash | delete.
  function RowMenu({ row, rect, onPick, onClose }) {
    if (!row || !rect) return null;
    const isTrash = row.status === "trash";
    const isDraft = !isTrash && row.status !== "published";
    const pick = (kind) => { onClose(); onPick(kind, row); };
    return (
      <>
        <div className="ml-scrim" onClick={onClose} />
        <div className="ml-menu" role="menu" style={{ position: "fixed", top: Math.round(rect.bottom + 6), left: Math.round(rect.right - 200), minWidth: 200 }}>
          {!isTrash && <button className="ml-menu__item" onClick={() => pick("edit")}><MlIcon name="edit" size={16} /> Edit</button>}
          {isDraft && <button className="ml-menu__item" onClick={() => pick("publish")}><MlIcon name="check-circle" size={16} /> Publish</button>}
          {row.link && <button className="ml-menu__item" onClick={() => pick("view")}><MlIcon name="external-link" size={16} /> View on site</button>}
          <div className="ml-menu__sep" />
          {isTrash
            ? <button className="ml-menu__item ml-menu__item--danger" onClick={() => pick("delete")}><MlIcon name="trash" size={16} /> Delete permanently</button>
            : <button className="ml-menu__item ml-menu__item--danger" onClick={() => pick("trash")}><MlIcon name="trash" size={16} /> Move to trash</button>}
        </div>
      </>
    );
  }

  // Confirm copy per destructive/irreversible action.
  const ROW_CONFIRM = {
    publish: (t, noun = "post") => ({ danger: false, title: `Publish this ${noun}?`, body: `“${t}” goes live on the site immediately.`, confirmLabel: "Publish" }),
    trash:   (t) => ({ danger: true,  title: "Move to trash?", body: `“${t}” moves to the WordPress trash. You can restore it there for 30 days.`, confirmLabel: "Move to trash" }),
    delete:  (t) => ({ danger: true,  title: "Delete permanently?", body: `“${t}” will be deleted from WordPress for good. This cannot be undone.`, confirmLabel: "Delete permanently" }),
  };

  function statusBadge(status) {
    const { Badge } = window.HuddleDesignSystem_775113;
    if (status === "published") return <Badge tone="success" dot>Published</Badge>;
    if (status === "review") return <span className="hds-badge hds-badge--info"><span className="hds-badge__dot" />In review</span>;
    return <span className="hds-badge hds-badge--draft"><span className="hds-badge__dot" />Draft</span>;
  }

  function seoChip(score) {
    const tone = score >= 75 ? "success" : score >= 50 ? "warning" : "danger";
    const cls = tone === "success" ? "hds-badge--success" : tone === "warning" ? "hds-badge--warning" : "hds-badge--danger";
    return <span className={"hds-badge " + cls} title="SEO score">{score}</span>;
  }

  // ---- Column sorting (shared by Posts and Pages) -----------------------
  // Status sorts by where a post IS in its life, not by the alphabet:
  // published, in review, draft, trash is the pipeline a post travels, while
  // "draft, published, review, trash" is just how those four words happen to
  // be spelled.
  const STATUS_RANK = { published: 0, review: 1, draft: 2, trash: 3 };
  const byText = (a, b) => String(a || "").toLowerCase().localeCompare(String(b || "").toLowerCase());
  const rank = (st) => (STATUS_RANK[st] != null ? STATUS_RANK[st] : 9);
  const SORTERS = {
    title:   (a, b) => byText(a.title, b.title),
    author:  (a, b) => byText(a.author, b.author),
    status:  (a, b) => rank(a.status) - rank(b.status),
    // Only ever offered at the All position, where a row has a section to name.
    section: (a, b) => byText(a.sectionName, b.sectionName),
    // And only where a list can hold both lenses, which is Pages.
    readFrom: (a, b) => byText(a.readFrom, b.readFrom),
    // The modified date, which is what this column has always been labelled.
    updated: (a, b) => String(a.modifiedRaw || "").localeCompare(String(b.modifiedRaw || "")),
    published: (a, b) => String(a.dateRaw || "").localeCompare(String(b.dateRaw || "")),
  };
  // A text column opens ascending (A first) and a date column descending
  // (newest first), because that is the order each one gets asked for.
  const SORT_DIR0 = { title: "asc", section: "asc", readFrom: "asc", author: "asc", status: "asc", updated: "desc", published: "desc" };
  // On a COPY: `rows` is derived from the loaded lists on every render, and
  // sorting in place would quietly reorder the state behind it.
  function sortRows(rows, sort) {
    const fn = SORTERS[sort && sort.key];
    if (!fn) return rows;
    const out = rows.slice().sort(fn);
    return sort.dir === "desc" ? out.reverse() : out;
  }
  function nextSort(sort, key) {
    return sort.key === key
      ? { key, dir: sort.dir === "asc" ? "desc" : "asc" }
      : { key, dir: SORT_DIR0[key] || "asc" };
  }
  // A sortable header. The arrow is always drawn, faint until the column is the
  // one in use: a sort control nobody can see is a sort control nobody presses,
  // which is how this table shipped looking unsortable.
  function SortTh({ id, label, sort, onSort, style }) {
    const on = sort.key === id;
    return (
      <th style={style} aria-sort={on ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}>
        <button type="button" className={"ml-th-sort" + (on ? " ml-th-sort--on" : "")}
          onClick={() => onSort(id)} title={`Sort by ${label.toLowerCase()}`}>
          {label}<MlIcon name={on && sort.dir === "asc" ? "chevron-up" : "chevron-down"} size={14} />
        </button>
      </th>
    );
  }

  // ---- Filters popover (Posts and Pages) --------------------------------
  const EMPTY_FILTERS = { author: "", cat: "", tag: "", from: "", to: "" };
  const countFilters = (f) => Object.keys(EMPTY_FILTERS).filter(k => f[k]).length;
  // Every filter matches on the row's own ids, so a post keeps its author and
  // its terms even when the name lists could not be read. `from`/`to` are whole
  // days compared as text, which holds because both sides are ISO yyyy-mm-dd.
  function passesFilters(p, f) {
    if (f.author && String(p.authorId || "") !== String(f.author)) return false;
    if (f.cat && !(p.cats || []).includes(Number(f.cat))) return false;
    if (f.tag && !(p.tags || []).includes(Number(f.tag))) return false;
    const day = String(p.modifiedRaw || "").slice(0, 10);
    if (f.from && (!day || day < f.from)) return false;
    if (f.to && (!day || day > f.to)) return false;
    return true;
  }
  // The option lists are read the first time this panel opens rather than on
  // page load: most visits never filter, and the reads need an editor login the
  // site may never have given us. A list that could not be read says so and
  // disables its own control, instead of rendering as an empty dropdown that
  // reads as "this site has no authors".
  function FiltersPopover({ rect, value, onChange, opts, onClose, showTerms }) {
    const { Select } = window.HuddleDesignSystem_775113;
    const W = 320;
    const vw = window.innerWidth || 1200;
    const left = Math.max(12, Math.min(Math.round(rect ? rect.left : 12), vw - W - 12));
    const top = Math.round((rect ? rect.bottom : 60) + 6);
    const set = (k, v) => onChange({ ...value, [k]: v });
    const missing = (what, err) => (
      <p className="ml-filterpop__note">{what} could not be read{err ? `: ${err}` : ""}.</p>
    );
    const pick = (key, label, list, err, anyLabel) => (
      <div className="ml-filterpop__f">
        <span className="ml-filterpop__lbl">{label}</span>
        {opts.loading
          ? <p className="ml-filterpop__note">Reading…</p>
          : (
            <>
              <Select value={value[key]} disabled={!list.length} onChange={e => set(key, e.target.value)}>
                <option value="">{anyLabel}</option>
                {list.map(o => <option key={o.id} value={String(o.id)}>{o.name}</option>)}
              </Select>
              {!list.length && missing(`The ${label.toLowerCase()} list`, err)}
            </>
          )}
      </div>
    );
    return (
      <>
        <div className="ml-scrim" onClick={onClose} />
        <div className="ml-filterpop" role="dialog" aria-label="Filters" style={{ position: "fixed", top, left, width: W }}>
          {pick("author", "Author", opts.authors, opts.authorsErr, "Any author")}
          {showTerms && pick("cat", "Category", opts.cats, opts.catsErr, "Any category")}
          {showTerms && pick("tag", "Tag", opts.tags, opts.tagsErr, "Any tag")}
          <div className="ml-filterpop__f">
            <span className="ml-filterpop__lbl">Updated between</span>
            <div className="ml-filterpop__dates">
              <input type="date" className="hds-input" aria-label="Updated on or after" value={value.from} max={value.to || undefined} onChange={e => set("from", e.target.value)} />
              <input type="date" className="hds-input" aria-label="Updated on or before" value={value.to} min={value.from || undefined} onChange={e => set("to", e.target.value)} />
            </div>
          </div>
          <div className="ml-filterpop__foot">
            <button type="button" className="hds-btn hds-btn--sm hds-btn--ghost" onClick={() => onChange({ ...EMPTY_FILTERS })} disabled={!countFilters(value)}>Clear all</button>
            <div style={{ flex: 1 }} />
            <button type="button" className="hds-btn hds-btn--sm hds-btn--secondary" onClick={onClose}>Done</button>
          </div>
        </div>
      </>
    );
  }

  // The option lists behind the popover, read once per site. Each read is
  // allowed to fail on its own: an author list that needs credentials must not
  // take the category list down with it.
  //
  // Read with the screen rather than on first open of the panel, because the
  // Author COLUMN resolves its names from the same list. A post carries an
  // author id, not a name, which is why that column used to show "-" on every
  // row of every site.
  const BLANK_OPTS = { loading: false, read: false, authors: [], cats: [], tags: [], authorsErr: "", catsErr: "", tagsErr: "" };
  function useFilterOptions(siteUrl, siteId) {
    const [opts, setOpts] = useState(BLANK_OPTS);
    useEffect(() => {
      if (!siteUrl) { setOpts(BLANK_OPTS); return; }
      let alive = true;
      setOpts({ ...BLANK_OPTS, loading: true });
      const enc = encodeURIComponent(siteUrl);
      const named = (arr) => (Array.isArray(arr) ? arr : (arr && arr.terms) || [])
        .map(t => ({ id: Number(t.id), name: String(t.name || "").trim() }))
        .filter(t => t.id && t.name)
        .sort((a, b) => byText(a.name, b.name));
      const read = (path) => api(path).then(r => ({ ok: r })).catch(e => ({ err: e.message || "read failed" }));
      const noId = Promise.resolve({ err: "the site id is not known here" });
      Promise.all([
        read(`/wp/users?siteUrl=${enc}`),
        siteId == null ? noId : read(`/sites/${siteId}/categories?siteUrl=${enc}`),
        siteId == null ? noId : read(`/sites/${siteId}/tags?siteUrl=${enc}`),
      ]).then(([u, c, t]) => {
        if (!alive) return;
        setOpts({
          loading: false, read: true,
          authors: u.ok ? named(u.ok) : [], authorsErr: u.err || "",
          cats: c.ok ? named(c.ok) : [], catsErr: c.err || "",
          tags: t.ok ? named(t.ok) : [], tagsErr: t.err || "",
        });
      });
      return () => { alive = false; };
    }, [siteUrl, siteId]);
    return opts;
  }

  /* Which read(s) a tab is showing, so the report panel describes the rows the
     customer is actually looking at. */
  const TAB_STATUSES = { all: ["publish", "draft"], published: ["publish"], draft: ["draft"], trash: ["trash"] };
  const STATUS_LABEL = { publish: "published", draft: "drafts", trash: "trash" };

  /* ══ A SECTION module at the All position ═══════════════════════════════
     docs/MODULE-SCOPE.md 1.1, second bullet: "A section module ENUMERATES at
     All. Its All position is a union list where every row names its section,
     and it offers NO TOTAL, because a total over three sections with three
     different CMSs is a number nobody asked for. A section filter removes the
     Section column and nothing else."

     These four helpers are shared by Posts, Pages, Categories, Tags and the
     Content Planner, which is the whole reason they are here rather than
     copied five times: five copies of "how many pieces answered" is five
     chances for one screen to look complete while another says it is not.

     WHAT IS AND IS NOT A TOTAL, because this is the line the panel walks. The
     count it prints is OF THIS ANSWER ("24 rows on screen, from 3 pieces"),
     which is a fact about the screen, and the per piece counts beside it are
     facts about pieces. What is never printed is a sum presented as a figure
     about the website: no "87 posts", no tab counts over the union, no
     "showing 24 of 87". The same reading the Bulk Meta Editor shipped under
     this document.

     AND THE COUNTS COME FROM LIVE STATE. A per section figure is counted from
     the rows ON SCREEN, never from the response's own number: a list capped
     for a read sets the ORDER, and a count shown to a person has to come from
     what is in front of them. */
  const VERB_LABEL = {
    edit: "Edit", publish: "Publish", manage: "Create, rename, delete",
    // MODULE-SCOPE 5: at a piece with no CMS these screens are "present and
    // empty, with the reason named", and the verb is nothing to press. The
    // Content Planner is the exception and overrides it, because a plan CAN
    // target a piece with no CMS, as a brief rather than a publish target.
    handover_brief: "Nothing to press here",
  };

  /* The per piece report, with each piece's live row count folded in.

     `has` is how a row says it belongs to a piece, and the taxonomy table needs
     its own: a term of a pool shared by two sections is ONE row listed under
     BOTH, so a row there carries a list of sections rather than one. Default is
     the single section every other screen's rows carry. */
  const OWNED_BY = (row, id) => row.sectionId === id;
  function sectionsLive(report, rows, has) {
    const owns = has || OWNED_BY;
    return ((report && report.sections) || []).map(s => ({
      ...s,
      live: rows.filter(r => owns(r, s.section_id)).length,
    }));
  }

  /* The panel that says what this answer is: how many rows, from how many
     pieces, which pieces answered, and that there is no total. Every sentence
     in it is the endpoint's own, rendered verbatim, for the reason the Coverage
     Map writes out for page gaps: a second opinion in the client is how one
     screen comes to disagree with another about what a website is. */
  function UnionReport({ subject, report, rows, verbs, has, children }) {
    if (!report) return null;
    const live = sectionsLive(report, rows, has);
    const labels = { ...VERB_LABEL, ...(verbs || {}) };
    const answered = report.answered || null;
    return (
      <div className="bm-kw-panel" style={{ marginBottom: 18 }}>
        <div className="bm-kw-row">
          <span className="bm-kw-lab">The website</span>
          <span className="bm-kw-note">
            {rows.length} {subject} row(s) on screen, from {live.length} piece(s). {report.sections_why}
          </span>
        </div>
        {live.map(s => (
          <div className="bm-kw-row" key={s.section_id}>
            <span className="bm-kw-chip">{s.section_name}</span>
            <span className={"bm-kw-note" + (s.error ? " bm-kw-note--bad" : "")}>
              {s.live} row(s){s.capped ? ", which is all one read takes, so there are more" : ""}
              {" · "}{labels[s.verb] || s.verb}
              {s.error ? ` · could not be read: ${s.error}` : ""}
              {s.why ? ` ${s.why}` : ""}
            </span>
          </div>
        ))}
        {answered && <p className="bm-kw-note">{answered.why}</p>}
        {report.no_total_why && <p className="bm-kw-note">{report.no_total_why}</p>}
        {children}
        {report.scope && (
          <p className="bm-kw-note">
            Answered at the <b>{report.scope.kind}</b>
            {report.scope.property_why ? `. ${report.scope.property_why}` : "."}
            {report.scope.read_from_why ? ` ${report.scope.read_from_why}` : ""}
          </p>
        )}
      </div>
    );
  }

  /* One piece being down is not the website being down. The rows that did
     arrive are on screen and the piece that failed is named here, which is the
     difference between an incomplete answer and an answer that looks complete.

     IT TAKES EVERY READ, not the one the panel above is describing. Posts and
     Pages read each status separately, exactly as they do per connection, so a
     piece can answer for published content and refuse the drafts: the trash
     needs an editor login many connections have never given us. A banner built
     from one of those reads would show a complete screen over a piece that
     half refused. The status is named with the piece, because "no drafts" and
     "would not show us its drafts" are different answers. */
  function BrokenSections({ reads, subject }) {
    const broken = [];
    for (const { label, report } of (reads || [])) {
      for (const s of ((report && report.sections) || [])) {
        if (s.error) broken.push({ ...s, label });
      }
    }
    if (!broken.length) return null;
    const { Banner } = window.HuddleDesignSystem_775113;
    const pieces = new Set(broken.map(s => s.section_id)).size;
    return (
      <div style={{ marginBottom: 18 }}>
        {React.createElement(Banner, { tone: "warning", title: pieces === 1
          ? "One piece of this website could not be read in full"
          : pieces + " pieces of this website could not be read in full" },
        <div>
          <ul className="bm-blocked">
            {broken.map(s => <li key={String(s.section_id) + s.label}><b>{s.section_name}</b> ({s.label}): {s.error}. {s.why}</li>)}
          </ul>
          <p style={{ margin: "8px 0 0" }}>
            Every other piece's rows are listed as usual, so the {subject} above are part of this website and not all
            of it. This is a connection problem rather than an empty section.
          </p>
        </div>)}
      </div>
    );
  }

  // The Section cell. One place, so the column reads the same on five screens.
  function SectionCell({ row }) {
    return (
      <td className="hds-table__muted">
        <span className="bm-kw-chip">{row.sectionName || "not named"}</span>
      </td>
    );
  }

  /* ---- Posts list - wired to the live backend ----
     THE SWITCHER IS A FILTER AND IT HAS AN ALL POSITION (main.jsx says the same
     where these two pieces of state are made): `site` is the ANCHOR
     CONNECTION, which every request carries so the server can authorise it and
     derive the property itself; `sectionId` is the FILTER, null at All and a
     section's own site_id otherwise. `sections` is this property's pieces.

     At ONE SECTION this screen is byte for byte what it has always been: the
     same three reads of the same connection, the same tabs with the same
     counts, the same single Save path. That is 1.1's last sentence, and it is
     what makes this change safe. At All it enumerates: one list, every row
     naming its piece and carrying what can be done to it there, and no total. */
  function PostsScreen({ site, sectionId, sections, onEdit, onGenerate }) {
    const { Button, IconButton, EmptyState } = window.HuddleDesignSystem_775113;
    const [tab, setTab] = useState("all");
    const [selected, setSelected] = useState([]);            // post ids
    const [lists, setLists] = useState({ publish: [], draft: [], trash: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [truncated, setTruncated] = useState(false);
    const [search, setSearch] = useState("");
    const [busy, setBusy] = useState(false);
    const [menu, setMenu] = useState(null);        // { row, rect }: open ⋯ menu
    const [confirm, setConfirm] = useState(null);  // { kind, row }: pending action
    const [sort, setSort] = useState({ key: "updated", dir: "desc" });
    const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
    const [filterAt, setFilterAt] = useState(null); // the button's rect while the panel is open

    const siteUrl = siteUrlOf(site);
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    const atAll = sectionId == null;
    // The three reads' own reports, one per status, kept apart for the reason
    // BrokenSections gives: a piece can answer for published content and refuse
    // the drafts. Null at a section, which has no pieces to report.
    const [reports, setReports] = useState({ publish: null, draft: null, trash: null });
    const opts = useFilterOptions(siteUrl, siteId);
    const nFilters = countFilters(filters);

    const load = useCallback(async () => {
      if (atAll) {
        if (siteId == null) { setLists({ publish: [], draft: [], trash: [] }); setReports({ publish: null, draft: null, trash: null }); setTruncated(false); setLoading(false); return; }
        setLoading(true); setError(null);
        // One read per status, as per connection, so the tabs mean the same
        // thing at both positions. A read that fails whole is its own report
        // rather than an empty screen: the endpoint already keeps one piece's
        // failure off the others, and this keeps one STATUS's failure off the
        // others.
        const blank = (e) => ({ items: [], sections: [], answered: null, sections_why: "", error: (e && e.message) || "could not be read" });
        try {
          const q = `/wp/content-property?site_id=${siteId}&type=posts`;
          const [publish, draft, trash] = await Promise.all([
            api(`${q}&status=publish`).catch(blank),
            api(`${q}&status=draft`).catch(blank),
            api(`${q}&status=trash`).catch(blank),
          ]);
          setLists({
            publish: ((publish && publish.items) || []).map(mapUnionPost),
            draft: ((draft && draft.items) || []).map(mapUnionPost),
            trash: ((trash && trash.items) || []).map(mapUnionPost),
          });
          setReports({ publish, draft, trash });
          setTruncated([publish, draft, trash].some(r => ((r && r.sections) || []).some(s => s.capped)));
        } catch (e) { setError(e.message || "Failed to load posts"); }
        finally { setLoading(false); }
        return;
      }
      if (!siteUrl) { setLists({ publish: [], draft: [], trash: [] }); setTruncated(false); setLoading(false); return; }
      setLoading(true); setError(null);
      const enc = encodeURIComponent(siteUrl);
      // apiList pages through WordPress, so the table holds every post on the
      // site, not the first slice. A status the site won't serve (trash needs
      // credentials) degrades to an empty tab rather than failing the whole load.
      const empty = { items: [], truncated: false };
      try {
        const [publish, draft, trash] = await Promise.all([
          apiList(`/wp/posts?siteUrl=${enc}&status=publish`).catch(() => empty),
          apiList(`/wp/posts?siteUrl=${enc}&status=draft`).catch(() => empty),
          apiList(`/wp/posts?siteUrl=${enc}&status=trash`).catch(() => empty),
        ]);
        // Through the same shape the union uses, with no section on it, so one
        // table reads one kind of row at both positions.
        const own = (x) => withSection(mapPost(x), {});
        setLists({
          publish: publish.items.map(own),
          draft: draft.items.map(own),
          trash: trash.items.map(own),
        });
        setReports({ publish: null, draft: null, trash: null });
        setTruncated(publish.truncated || draft.truncated || trash.truncated);
      } catch (e) { setError(e.message || "Failed to load posts"); }
      finally { setLoading(false); }
    }, [atAll, siteId, siteUrl]);

    useEffect(() => { setSelected([]); load(); }, [load]);

    const counts = {
      all: lists.publish.length + lists.draft.length,
      published: lists.publish.length,
      draft: lists.draft.length,
      trash: lists.trash.length,
    };
    const base = tab === "published" ? lists.publish
      : tab === "draft" ? lists.draft
      : tab === "trash" ? lists.trash
      : [...lists.publish, ...lists.draft];
    const term = search.trim().toLowerCase();
    // One pass, in the order it reads: name the author (so the column and the
    // sort see the same value), then search, then filter, then sort. The All
    // tab's own date ordering is gone, because `sort` owns the order now and
    // starts on the same thing.
    const authorById = {};
    for (const a of opts.authors) authorById[String(a.id)] = a.name;
    const named = base.map(p => (p.authorId != null && authorById[String(p.authorId)]
      ? { ...p, author: authorById[String(p.authorId)] } : p));
    const searched = term ? named.filter(p => (p.title || "").toLowerCase().includes(term)) : named;
    const filtered = nFilters ? searched.filter(p => passesFilters(p, filters)) : searched;
    const rows = sortRows(filtered, sort);
    const onSort = (key) => setSort(s2 => nextSort(s2, key));

    // The report of the read this tab is showing, and EVERY read for the
    // banner: a piece that answered for published posts and refused the trash
    // has to be named whichever tab is open.
    const tabReport = atAll ? (reports[(TAB_STATUSES[tab] || ["publish"])[0]] || null) : null;
    const reads = atAll
      ? ["publish", "draft", "trash"].map(k => ({ label: STATUS_LABEL[k], report: reports[k] })).filter(x => x.report)
      : [];
    const heldBack = rows.filter(p => selected.includes(p.rowKey) && !p.canEdit).length;

    /* NO TOTAL AT ALL (MODULE-SCOPE 1.1). The tab counts are a sum over the
       pieces, and three CMSs do not add up to a figure anybody asked for, so
       at All the tabs carry no number and the per piece counts live in the
       report panel instead. At a section they are exactly what they were. */
    const tabs = [
      { id: "all", label: "All", count: atAll ? null : counts.all },
      { id: "published", label: "Published", count: atAll ? null : counts.published },
      { id: "draft", label: "Drafts", count: atAll ? null : counts.draft },
      { id: "trash", label: "Trash", count: atAll ? null : counts.trash },
    ];

    // Keyed on rowKey, never on the post id: see withSection.
    const allChecked = rows.length > 0 && rows.every(p => selected.includes(p.rowKey));
    const toggleAll = () => setSelected(allChecked ? [] : rows.map(p => p.rowKey));
    const toggleOne = (key) => setSelected(s => s.includes(key) ? s.filter(x => x !== key) : [...s, key]);
    // The address a write goes to is the ROW's, and the anchor's is the
    // fallback only where there is no union: at a section every row came from
    // this connection.
    const urlOf = (row) => row.siteUrl || siteUrl;

    /* The bulk bar acts on the ticked rows THAT CAN BE WRITTEN and names the
       ones it held back, which is what the Bulk Meta Editor already does. At
       All a read-only row cannot appear here (a piece with no CMS holds no
       posts), and the guard is kept anyway: it costs one filter and it is the
       difference between holding a row back and writing it to the wrong
       place. */
    const bulk = async (fn) => {
      const picked = rows.filter(p => selected.includes(p.rowKey) && p.canEdit && urlOf(p));
      if (!picked.length) return;
      setBusy(true);
      try { for (const row of picked) await fn(row); setSelected([]); await load(); }
      catch (e) { setError(e.message || "Action failed"); }
      finally { setBusy(false); }
    };
    const bulkPublish = () => bulk(row => api(`/wp/posts/${row.id}`, { method: "PUT", body: JSON.stringify({ siteUrl: urlOf(row), status: "publish" }) }));
    const bulkTrash = () => bulk(row => api(`/wp/posts/${row.id}?siteUrl=${encodeURIComponent(urlOf(row))}&force=false`, { method: "DELETE" }));

    // Row ⋯ menu → Edit / Publish / View / Trash / Delete permanently.
    const pickRowAction = (kind, row) => {
      if (kind === "edit") { onEdit && onEdit(row, row.sectionId); return; }
      if (kind === "view") { row.link && window.open(row.link, "_blank", "noopener"); return; }
      setConfirm({ kind, row });
    };
    const runRowAction = async () => {
      if (!confirm) return;
      const { kind, row } = confirm;
      setConfirm(null); setBusy(true); setError(null);
      const target = urlOf(row);
      const enc = encodeURIComponent(target);
      try {
        if (kind === "publish") await api(`/wp/posts/${row.id}`, { method: "PUT", body: JSON.stringify({ siteUrl: target, status: "publish" }) });
        if (kind === "trash")   await api(`/wp/posts/${row.id}?siteUrl=${enc}&force=false`, { method: "DELETE" });
        if (kind === "delete")  await api(`/wp/posts/${row.id}?siteUrl=${enc}&force=true`, { method: "DELETE" });
        await load();
      } catch (e) { setError(e.message || "Action failed"); }
      finally { setBusy(false); }
    };

    return (
      <div>
        <div className="ml-pagehead">
          <div className="ml-pagehead__titles">
            <h1 className="ml-pagehead__title">Posts
              <span className="ml-draftnotice"><MlIcon name="info" size={14} /> All saves go to Draft</span>
            </h1>
            <p className="ml-pagehead__sub">
              {atAll
                ? <>Every post of this website, each row naming the piece that publishes it. Publishing is always an explicit action.</>
                : <>Draft, review and publish posts on <strong>{site.name}</strong>. Publishing is always an explicit action.</>}
            </p>
          </div>
          <div className="ml-pagehead__actions">
            <Button variant="ai" leadingIcon={<MlIcon name="sparkles" size={18} />} size="md" onClick={() => onGenerate && onGenerate()}>Generate article</Button>
            {/* A new post needs ONE CMS to be created in, and at All the
                switcher is naming no piece. Rather than pick one for the
                customer, the button says to choose, which is the reasoning that
                keeps Save per piece on the Bulk Meta Editor. */}
            <Button variant="primary" leadingIcon="plus" size="md" disabled={atAll}
              title={atAll ? "Pick the piece of the website to write in from the switcher first: a new post has to be created in one CMS." : undefined}
              onClick={() => onEdit && onEdit(null)}>New post</Button>
          </div>
        </div>

        {atAll && <UnionReport subject="post" report={tabReport} rows={rows} />}
        {atAll && <BrokenSections reads={reads} subject="posts" />}

        <div className="hds-table-wrap">
          {/* tabs bar */}
          <div style={{ padding: "0 16px" }}>
            <div className="hds-tabs">
              {tabs.map(t => (
                <button key={t.id} className={["hds-tab", tab === t.id ? "hds-tab--active" : ""].filter(Boolean).join(" ")} onClick={() => { setTab(t.id); setSelected([]); }}>
                  {t.label}{t.count == null ? null : <span className="hds-tab__count">{t.count}</span>}
                </button>
              ))}
            </div>
          </div>

          {/* toolbar: search + filters OR bulk actions */}
          {selected.length === 0 ? (
            <div className="ml-table-toolbar">
              <div className="ml-table-search">
                <div className="hds-input-group">
                  <span className="hds-input-group__icon"><MlIcon name="search" size={16} /></span>
                  <input className="hds-input" placeholder="Search posts" aria-label="Search posts" value={search} onChange={e => setSearch(e.target.value)} />
                </div>
              </div>
              <Button variant="secondary" size="sm" leadingIcon="filter"
                onClick={e => setFilterAt(filterAt ? null : e.currentTarget.getBoundingClientRect())}>
                Filters{nFilters ? ` (${nFilters})` : ""}
              </Button>
              {nFilters > 0 && (
                <button type="button" className="hds-btn hds-btn--sm hds-btn--ghost" onClick={() => setFilters({ ...EMPTY_FILTERS })}>Clear</button>
              )}
              <div style={{ flex: 1 }} />
              {/* Under a filter, "12 posts" on its own reads as the whole tab.
                  At All it is a count of what is ON SCREEN and says so, because
                  a sum over three CMSs is not a figure about the website. */}
              <span style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>
                {rows.length} {rows.length === 1 ? "post" : "posts"}{nFilters || term ? ` of ${base.length}` : ""}
                {atAll ? " on screen" : ""}
              </span>
            </div>
          ) : (
            <div className="ml-table-toolbar ml-table-toolbar--bulk">
              <strong style={{ fontSize: "var(--text-sm)" }}>{selected.length} selected</strong>
              {heldBack > 0 && (
                <span style={{ fontSize: "var(--text-sm)", color: "var(--text-muted)" }}>
                  {heldBack} of them cannot be written and are held back
                </span>
              )}
              <div style={{ flex: 1 }} />
              <Button variant="secondary" size="sm" leadingIcon="check-circle" disabled={busy} onClick={bulkPublish}>Publish</Button>
              <Button variant="secondary" size="sm" leadingIcon="trash" disabled={busy} onClick={bulkTrash}>Move to trash</Button>
            </div>
          )}

          {loading ? (
            <div style={{ padding: "48px 24px", textAlign: "center", color: "var(--text-muted)", fontSize: "var(--text-sm)" }}>Loading posts…</div>
          ) : error ? (
            <div style={{ padding: "48px 24px" }}>
              {React.createElement(EmptyState, { icon: "alert-triangle", title: "Couldn't load posts", description: error })}
            </div>
          ) : rows.length === 0 ? (
            <div style={{ padding: "64px 24px" }}>
              {React.createElement(EmptyState, tab === "trash"
                ? { icon: "trash", title: "Trash is empty", description: "Posts you move to trash will appear here for 30 days before they're permanently deleted." }
                : term || nFilters
                  ? { icon: "search", title: "No matches", description: nFilters && term ? "No posts match your search and these filters." : nFilters ? "No posts match these filters." : "No posts match your search." }
                  : atAll
                    ? { icon: "file-text", title: "No posts on this website yet", description: "No piece of this website holds a post under this tab. The pieces that answered are listed above, with what each one contributed." }
                    : { icon: "file-text", title: "No posts yet", description: "Posts you create will appear here. New posts always start as drafts." })}
            </div>
          ) : (
            <table className="hds-table">
              <thead>
                <tr>
                  <th className="ml-th-check">
                    <label className="hds-check"><input type="checkbox" checked={allChecked} onChange={toggleAll} /><span className="hds-check__box"><MlIcon name="check" size={12} /></span></label>
                  </th>
                  <SortTh id="title" label="Title" sort={sort} onSort={onSort} />
                  {/* THE SECTION COLUMN, and a section filter removes it and
                      nothing else (MODULE-SCOPE 1.1). Sortable, because the
                      first thing anybody does with a union list is group it. */}
                  {atAll && <SortTh id="section" label="Section" sort={sort} onSort={onSort} />}
                  <SortTh id="author" label="Author" sort={sort} onSort={onSort} />
                  {/* SEO and Words are not in the list response, so there is
                      nothing to order them by. No sort affordance is offered,
                      rather than one that silently does nothing. */}
                  <th>SEO</th>
                  <th>Words</th>
                  <SortTh id="status" label="Status" sort={sort} onSort={onSort} />
                  <SortTh id="updated" label="Updated" sort={sort} onSort={onSort} />
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr key={p.rowKey} className={p.status !== "published" ? "ml-row--draft" : ""} style={{ cursor: p.canEdit ? "pointer" : "default" }} onClick={() => p.canEdit && onEdit && onEdit(p, p.sectionId)}>
                    <td className="ml-checkcell" onClick={e => e.stopPropagation()}>
                      <label className="hds-check"><input type="checkbox" checked={selected.includes(p.rowKey)} onChange={() => toggleOne(p.rowKey)} /><span className="hds-check__box"><MlIcon name="check" size={12} /></span></label>
                    </td>
                    <td>
                      <div className="ml-rowtitle">{p.title}</div>
                      <div className="ml-rowsub">{p.slug}</div>
                    </td>
                    {atAll && <SectionCell row={p} />}
                    <td className="hds-table__muted">{p.author || "-"}</td>
                    <td>{p.seo == null ? <span className="hds-table__muted">-</span> : seoChip(p.seo)}</td>
                    <td className="hds-table__muted" style={{ fontVariantNumeric: "tabular-nums" }}>{p.words == null ? "-" : p.words.toLocaleString()}</td>
                    <td>{statusBadge(p.status)}</td>
                    <td className="hds-table__muted">{p.updated}</td>
                    <td onClick={e => e.stopPropagation()}>
                      <div className="ml-rowactions">
                        {/* The verb is the ROW's (1.2). A row from a piece with
                            no CMS is read only and the tooltip says why. */}
                        <IconButton icon="edit" label={p.canEdit ? "Edit" : "Read only"} size="sm" disabled={!p.canEdit}
                          title={p.canEdit ? undefined : (p.verbWhy || "")}
                          onClick={() => p.canEdit && onEdit && onEdit(p, p.sectionId)} />
                        <IconButton icon="external-link" label="View" size="sm" onClick={() => p.link && window.open(p.link, "_blank", "noopener")} />
                        <IconButton icon="more-horizontal" label="More" size="sm" onClick={(e) => setMenu({ row: p, rect: e.currentTarget.getBoundingClientRect() })} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {tab !== "trash" && (
            <div className="ml-pagination">
              <span>
                {atAll
                  ? <>Showing {rows.length} row(s) from {((tabReport && tabReport.sections) || []).length} piece(s). No total: the panel above says why.</>
                  : <>Showing {rows.length} of {counts.all}</>}
                {truncated && <> · <strong>only the first 1,000 loaded</strong>: narrow by status or search</>}
              </span>
              <div className="ml-pagination__pages">
                <Button variant="secondary" size="sm" disabled leadingIcon="chevron-left">Prev</Button>
                <Button variant="secondary" size="sm" trailingIcon="chevron-right">Next</Button>
              </div>
            </div>
          )}
        </div>

        {filterAt && <FiltersPopover rect={filterAt} value={filters} onChange={setFilters} opts={opts} showTerms onClose={() => setFilterAt(null)} />}
        {menu && <RowMenu row={menu.row} rect={menu.rect} onClose={() => setMenu(null)} onPick={pickRowAction} />}
        {confirm && React.createElement(window.MlBiModals.ConfirmDialog, {
          ...ROW_CONFIRM[confirm.kind](confirm.row.title),
          onClose: () => setConfirm(null),
          onConfirm: runRowAction,
        })}
      </div>
    );
  }

  // ---- Generic stub for screens designed in a later stage
  function GenericStub({ id }) {
    const { Button } = window.HuddleDesignSystem_775113;
    const meta = (window.MlData.SCREEN_META[id]) || { title: id, sub: "" };
    const group = window.MlData.NAV_GROUPS.find(g => g.items.some(it => it.id === id));
    const item = group && group.items.find(it => it.id === id);
    return (
      <div>
        <div className="ml-pagehead">
          <div className="ml-pagehead__titles">
            <h1 className="ml-pagehead__title">{meta.title}</h1>
            <p className="ml-pagehead__sub">{meta.sub}</p>
          </div>
        </div>
        <div className="ml-stub">
          <span className="ml-stub__icon"><MlIcon name={item ? item.icon : "shapes"} size={26} /></span>
          <div className="ml-stub__title">{meta.title}</div>
          <p className="ml-stub__sub">This screen is built on the shell you're looking at now. The high-fidelity layout lands in a later stage.</p>
          <span className="ml-stub__stage">Coming in stage {meta.stage || "-"}</span>
        </div>
      </div>
    );
  }

  // The sorting and filtering tools are shared with the Pages table in
  // content.jsx, which is the same table over a different post type.
  window.MlScreens = { PostsScreen, GenericStub, RowMenu, ROW_CONFIRM,
    SortTh, sortRows, nextSort, FiltersPopover, useFilterOptions, EMPTY_FILTERS, countFilters, passesFilters,
    // The All position's shared parts (MODULE-SCOPE 1.1), used by Pages, the
    // taxonomy table and the Content Planner. One implementation, so five
    // screens cannot come to disagree about how many pieces answered.
    withSection, UnionReport, BrokenSections, SectionCell, sectionsLive, VERB_LABEL, TAB_STATUSES, STATUS_LABEL };
})();
